import {
	SvgIconName,
	IconAndText
} from "client/Components";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { ReactionCounterModel, ReactionKind } from "client/Api/Hub/common";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "../../Hub/useHubStatisticsFormatter";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";

type ReactionsCountButtonProps = {
	counters: readonly ReactionCounterModel[];
	maxAmountOfIconsToShow?: number;
	trackingId: string;
	onClick?(): void;
};

export const ReactionsCountButton = (props: ReactionsCountButtonProps): ReactElement => {
	const {
		counters,
		maxAmountOfIconsToShow,
		onClick,
		trackingId
	} = props;

	const formatter: NumberFormatter = useHubStatisticsFormatter();

	const { total, reactionsToRender } = useMemo(
		(): { total: number; reactionsToRender: readonly ReactionKind[]; } => {
			let {
				total,
				reactions: reactionsToRender
			} = getReactionSummary(counters);

			if (isNotNil(maxAmountOfIconsToShow)) {
				reactionsToRender = reactionsToRender.slice(0, maxAmountOfIconsToShow);
			}

			return { total, reactionsToRender };
		},
		[maxAmountOfIconsToShow, counters]
	);

	const renderIcons = (): ReactElement => {
		if (total === 0) {
			return (
				<IconAndText.Icon
					value="thumbs-up"
					trackingId={generateTrackingId("reaction-icon", `|kind-${ReactionKind.NoReaction}`)}
				/>
			);
		}

		return (
			<>
				{reactionsToRender.map(
					x => (
						<IconAndText.Icon
							key={x}
							value={{
								kind: "svg",
								name: ensureNotNil(
									reactionIconMap[x]
								)
							}}
							trackingId={generateTrackingId("reaction-icon", `|kind-${x}`)}
						/>
					)
				)}
			</>
		);
	};

	return (
		<IconAndText
			trackingId={trackingId}
			text={formatter(total)}
			onClick={onClick}
		>
			{renderIcons()}
		</IconAndText>
	);
};
ReactionsCountButton.displayName = "ReactionsCountButton";

export const reactionIconMap: Record<ReactionKind, SvgIconName | undefined> = {
	[ReactionKind.NoReaction]: undefined,
	[ReactionKind.Like]: "like",
	[ReactionKind.Celebrate]: "celebrate",
	[ReactionKind.Laugh]: "laugh",
	[ReactionKind.Love]: "love",
	[ReactionKind.Sad]: "sad",
	[ReactionKind.Thank]: "thank",
};

export type ReactionSummary = {
	total: number;
	reactions: readonly ReactionKind[];
};

export function getReactionSummary(
	counters: readonly ReactionCounterModel[]
): ReactionSummary {
	return [...counters]
		.sort(
			(a, b) => nativeSortFunction(
				a.count,
				b.count,
				DataSourceSortDirection.Descending
			)
		)
		.reduce<{ total: number; reactions: ReactionKind[]; }>(
			(acc, x) => {
				acc.total += x.count;
				acc.reactions
					.push(x.kind);

				return acc;
			},
			{ total: 0, reactions: [] }
		);
}
